import React, { useCallback, useEffect, useState } from "react";
import {
  IonContent,
  IonPage,
  IonText,
  IonSpinner,
  IonButton,
  IonBadge,
  IonAlert,
  IonIcon,
  useIonToast,
} from "@ionic/react";
import { callOutline } from "ionicons/icons";
import moment from "moment";
import Header from "src/components/Header";
import MisAsignacionesService from "src/services/misAsignaciones";
import OperariosRemplazos from "src/services/operarioRemplazo";

const formatDate = (value?: string) =>
  value ? moment(value).format("DD/MM/YYYY") : "-";

const formatTime = (value?: string) => {
  if (!value) return "-";
  const txt = String(value);
  return txt.length >= 5 ? txt.slice(0, 5) : txt;
};

const days = [
  { key: "lun", label: "LUN", fullLabel: "LUNES" },
  { key: "mar", label: "MAR", fullLabel: "MARTES" },
  { key: "mie", label: "MIE", fullLabel: "MIERCOLES" },
  { key: "jue", label: "JUE", fullLabel: "JUEVES" },
  { key: "vie", label: "VIE", fullLabel: "VIERNES" },
  { key: "sab", label: "SAB", fullLabel: "SABADO" },
  { key: "dom", label: "DOM", fullLabel: "DOMINGO" },
];

const hasDaySchedule = (item: any) =>
  days.some((d) => item[`${d.key}_ent`] || item[`${d.key}_sal`]);

// Formato compacto: si los minutos son 00 muestra "HH", sino "HH:MM"
const formatTimeShort = (value?: string) => {
  if (!value) return "";
  const txt = String(value);
  if (txt.length >= 5 && txt.substring(3, 5) === "00") {
    return txt.slice(0, 2);
  }
  return txt.slice(0, 5);
};

// Formato "HH:MMhs" (ej: "15:00hs"). Si no hay valor, vacio.
const formatTimeHs = (value?: string) => {
  if (!value) return "";
  const txt = String(value);
  const hhmm = txt.length >= 5 ? txt.slice(0, 5) : txt;
  return `${hhmm}hs`;
};

const renderScheduleTable = (item: any) => {
  // Filtramos solo los dias que tienen horario para no desbordar la card en mobile
  const diasActivos = days.filter((d) => item[`${d.key}_ent`] || item[`${d.key}_sal`]);
  if (diasActivos.length === 0) return null;
  // Con pocos dias activos hay espacio para mostrar el nombre completo
  const usarNombreCompleto = diasActivos.length <= 3;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(60px, 1fr))",
        gap: 6,
        marginTop: 6,
      }}
    >
      {diasActivos.map((d) => {
        const ent = item[`${d.key}_ent`];
        const sal = item[`${d.key}_sal`];
        return (
          <div
            key={`day-${d.key}`}
            style={{
              background: "#fdeef3",
              border: "1px solid #e0e0e0",
              borderRadius: 10,
              padding: "10px 4px",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
            }}
          >
            <div
              style={{
                color: "#910434",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {usarNombreCompleto ? d.fullLabel : d.label}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#333",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              {formatTimeHs(ent)}
            </div>
            <div style={{ fontSize: 12, color: "#910434", lineHeight: 1 }}>↓</div>
            <div
              style={{
                fontSize: 12,
                color: "#333",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              {formatTimeHs(sal)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TelefonoLink: React.FC<{ telefono?: string }> = ({ telefono }) => {
  const tel = String(telefono || "").trim();
  if (!tel) return null;
  return (
    <a
      href={`tel:${tel}`}
      style={{ color: "#910434", fontWeight: 600, textDecoration: "none", fontSize: 14 }}
    >
      <IonIcon icon={callOutline} style={{ fontSize: 14, verticalAlign: -2, marginRight: 4 }} />
      {tel}
    </a>
  );
};

const abrirEnMapa = (coordenadas?: string, direccion?: string) => {
  const coords = (coordenadas || "").trim();
  if (coords) {
    window.open(`https://www.google.com/maps?q=${encodeURIComponent(coords)}`, "_blank");
    return;
  }
  if (direccion) {
    window.open(`https://www.google.com/maps?q=${encodeURIComponent(direccion)}`, "_blank");
  }
};

// Agrupa los reemplazos por cabecera (cab_id + fuente) para mostrar una sola card
// con todos los cortes que el reemplazante debe cubrir.
const agruparReemplazos = (items: any[]): any[] => {
  const mapa = new Map<string, any>();
  for (const item of items || []) {
    const fuente = String(item.fuente || "").toUpperCase();
    const cabId = item.cab_id != null ? String(item.cab_id) : String(item.id || "");
    const clave = `${fuente}-${cabId}`;
    let grupo = mapa.get(clave);
    if (!grupo) {
      grupo = {
        cab_id: cabId,
        fuente,
        punto_servicio: item.punto_servicio,
        direccion_ps: item.direccion_ps,
        coordenadas: item.coordenadas,
        fiscal_nombre: item.fiscal_nombre,
        fiscal_telefono: item.fiscal_telefono,
        nombre_reemplazado: item.nombre_reemplazado,
        legajo_reemplazado: item.legajo_reemplazado,
        telefono_reemplazado: item.telefono_reemplazado,
        cortes: [],
        confirmaciones: [] as Array<{ id: number; estado: string }>,
      };
      mapa.set(clave, grupo);
    }
    grupo.cortes.push(item);
    if (item.confirmacion_id) {
      grupo.confirmaciones.push({
        id: Number(item.confirmacion_id),
        estado: String(item.estado_confirmacion || "").toUpperCase(),
      });
    }
  }
  // Estado consolidado del grupo + rango de fechas
  const resultado: any[] = [];
  for (const g of Array.from(mapa.values())) {
    const fechas = g.cortes
      .map((c: any) => c.fecha_inicio || c.fecha)
      .filter(Boolean)
      .sort();
    const fechasFin = g.cortes
      .map((c: any) => c.fecha_fin || c.fechaFin)
      .filter(Boolean)
      .sort();
    g.fecha_inicio = fechas[0] || "";
    g.fecha_fin = fechasFin[fechasFin.length - 1] || g.fecha_inicio;

    if (g.confirmaciones.length === 0) {
      g.estado_confirmacion_grupo = null;
    } else if (g.confirmaciones.every((c: any) => c.estado === "CONFIRMADO")) {
      g.estado_confirmacion_grupo = "CONFIRMADO";
    } else if (g.confirmaciones.every((c: any) => c.estado === "RECHAZADO")) {
      g.estado_confirmacion_grupo = "RECHAZADO";
    } else {
      g.estado_confirmacion_grupo = "PENDIENTE";
    }
    resultado.push(g);
  }
  // Ordenar grupos por fecha de inicio
  resultado.sort((a, b) => String(a.fecha_inicio).localeCompare(String(b.fecha_inicio)));
  return resultado;
};

const MisReemplazos: React.FC = () => {
  const [reemplazos, setReemplazos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmandoCabId, setConfirmandoCabId] = useState<string | null>(null);
  const [rechazandoCabId, setRechazandoCabId] = useState<string | null>(null);
  const [grupoARechazar, setGrupoARechazar] = useState<any | null>(null);
  const [present] = useIonToast();

  const grupos = agruparReemplazos(reemplazos);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await MisAsignacionesService.listar();
      setReemplazos((response as any)?.reemplazos || []);
    } catch (err) {
      console.error("Error al cargar mis reemplazos:", err);
      setError("No se pudieron cargar tus reemplazos.");
      setReemplazos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConfirmarGrupo = useCallback(async (grupo: any) => {
    const ids = (grupo.confirmaciones || [])
      .filter((c: any) => c.estado === "PENDIENTE")
      .map((c: any) => c.id);
    if (ids.length === 0 || confirmandoCabId) return;
    const cabClave = `${grupo.fuente}-${grupo.cab_id}`;
    setConfirmandoCabId(cabClave);
    try {
      const resultados = await Promise.all(
        ids.map((id: number) => OperariosRemplazos.confirmarRemplazo(id).catch((e) => ({ error: e })))
      );
      const exitosos = resultados.filter((r: any) => r?.data?.ok).length;
      const fallidos = ids.length - exitosos;

      // Actualizar estado local
      setReemplazos((prev) =>
        prev.map((r: any) =>
          ids.includes(Number(r.confirmacion_id))
            ? { ...r, estado_confirmacion: "CONFIRMADO" }
            : r
        )
      );

      if (fallidos === 0) {
        present({
          message: `Asistencia confirmada${ids.length > 1 ? ` (${ids.length} cortes)` : ""}.`,
          duration: 3000,
          color: "success",
        });
      } else {
        present({
          message: `Confirmados ${exitosos} de ${ids.length}. ${fallidos} con error.`,
          duration: 4000,
          color: "warning",
        });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || "No se pudo confirmar la asistencia.";
      present({ message: msg, duration: 4000, color: "danger" });
    } finally {
      setConfirmandoCabId(null);
    }
  }, [confirmandoCabId, present]);

  const handleRechazarGrupo = useCallback(async (grupo: any, motivo: string) => {
    const ids = (grupo?.confirmaciones || [])
      .filter((c: any) => c.estado === "PENDIENTE")
      .map((c: any) => c.id);
    const motivoTrim = String(motivo || "").trim();
    if (ids.length === 0 || rechazandoCabId) return;
    if (!motivoTrim) {
      present({ message: "El motivo del rechazo es obligatorio.", duration: 3000, color: "warning" });
      return;
    }
    const cabClave = `${grupo.fuente}-${grupo.cab_id}`;
    setRechazandoCabId(cabClave);
    try {
      const resultados = await Promise.all(
        ids.map((id: number) =>
          OperariosRemplazos.rechazarRemplazo(id, motivoTrim).catch((e) => ({ error: e }))
        )
      );
      const exitosos = resultados.filter((r: any) => r?.data?.ok).length;
      const fallidos = ids.length - exitosos;

      setReemplazos((prev) =>
        prev.map((r: any) =>
          ids.includes(Number(r.confirmacion_id))
            ? { ...r, estado_confirmacion: "RECHAZADO" }
            : r
        )
      );

      if (fallidos === 0) {
        present({
          message: `Reemplazo rechazado${ids.length > 1 ? ` (${ids.length} cortes)` : ""}.`,
          duration: 3000,
          color: "success",
        });
      } else {
        present({
          message: `Rechazados ${exitosos} de ${ids.length}. ${fallidos} con error.`,
          duration: 4000,
          color: "warning",
        });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || "No se pudo rechazar el reemplazo.";
      present({ message: msg, duration: 4000, color: "danger" });
    } finally {
      setRechazandoCabId(null);
    }
  }, [rechazandoCabId, present]);

  useEffect(() => {
    cargar();
  }, []);

  return (
    <IonPage>
      <Header titulo="Mis Reemplazos" />
      <IonContent id="mis-reemplazos">
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 40 }}>
            <IonSpinner />
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", marginTop: 40, padding: "0 16px" }}>
            <IonText color="danger">{error}</IonText>
          </div>
        ) : reemplazos.length === 0 ? (
          <div style={{ textAlign: "center", marginTop: 40, padding: "0 16px" }}>
            <IonText color="medium">Sin reemplazos vigentes.</IonText>
          </div>
        ) : (
          <div
            style={{
              padding: 16,
              minHeight: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 16,
              boxSizing: "border-box",
            }}
          >
            {grupos.map((grupo: any, idx: number) => {
              const tieneCoords = Boolean((grupo.coordenadas || "").trim());
              const tieneDireccion = Boolean((grupo.direccion_ps || "").trim());
              const estadoConf = String(grupo.estado_confirmacion_grupo || "").toUpperCase();
              const puedeMapa = tieneCoords || tieneDireccion;
              const expandirCard = grupos.length === 1;
              const tieneCortes = Array.isArray(grupo.cortes) && grupo.cortes.length > 0;
              const tieneTablaSemana =
                tieneCortes && grupo.cortes.some((c: any) => hasDaySchedule(c));
              const cabClave = `${grupo.fuente}-${grupo.cab_id}`;
              const pendientes = (grupo.confirmaciones || []).filter(
                (c: any) => c.estado === "PENDIENTE"
              );

              return (
                <div
                  key={`grp-${idx}`}
                  style={{
                    background: "#ffffff",
                    borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                    padding: 24,
                    flex: expandirCard ? 1 : "0 0 auto",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                      marginBottom: 18,
                    }}
                  >
                    <h2
                      style={{
                        margin: 0,
                        fontSize: 22,
                        fontWeight: 700,
                        color: "#910434",
                        lineHeight: 1.25,
                      }}
                    >
                      {grupo.punto_servicio || "-"}
                    </h2>
                    {estadoConf === "CONFIRMADO" ? (
                      <IonBadge color="success" style={{ fontSize: 13, padding: "6px 10px" }}>
                        Confirmado
                      </IonBadge>
                    ) : estadoConf === "RECHAZADO" ? (
                      <IonBadge color="danger" style={{ fontSize: 13, padding: "6px 10px" }}>
                        Rechazado
                      </IonBadge>
                    ) : estadoConf === "PENDIENTE" ? (
                      <IonBadge color="warning" style={{ fontSize: 13, padding: "6px 10px" }}>
                        Pendiente
                      </IonBadge>
                    ) : null}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: 14,
                      fontSize: 16,
                      color: "#222",
                      lineHeight: 1.4,
                    }}
                  >
                    {tieneDireccion ? (
                      <div>
                        <div style={{ fontSize: 13, color: "#777", textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Direccion
                        </div>
                        <div>{grupo.direccion_ps}</div>
                      </div>
                    ) : null}
                    <div>
                      <div style={{ fontSize: 13, color: "#777", textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Fecha
                      </div>
                      <div>
                        {formatDate(grupo.fecha_inicio)}
                        {grupo.fecha_fin && grupo.fecha_fin !== grupo.fecha_inicio
                          ? ` - ${formatDate(grupo.fecha_fin)}`
                          : ""}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 13, color: "#777", textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Ausente
                      </div>
                      <div>{grupo.nombre_reemplazado || grupo.legajo_reemplazado || "-"}</div>
                      <TelefonoLink telefono={grupo.telefono_reemplazado} />
                    </div>
                    {grupo.fiscal_nombre ? (
                      <div>
                        <div style={{ fontSize: 13, color: "#777", textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Fiscal a cargo
                        </div>
                        <div>{grupo.fiscal_nombre}</div>
                        <TelefonoLink telefono={grupo.fiscal_telefono} />
                      </div>
                    ) : null}
                  </div>

                  {tieneCortes ? (
                    <div style={{ marginTop: 18 }}>
                      <div style={{ fontSize: 13, color: "#777", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                        {grupo.cortes.length > 1 ? "Cortes a cubrir" : "Horario"}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {grupo.cortes.map((c: any, ci: number) => {
                          const fechaC = c.fecha_inicio || c.fecha;
                          const horaIni = c.hora_inicio;
                          const horaFin = c.hora_fin;
                          return (
                            <div
                              key={`cort-${idx}-${ci}`}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                background: "#f7f4f6",
                                borderRadius: 6,
                                padding: "8px 12px",
                                fontSize: 15,
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>{formatDate(fechaC)}</span>
                              <span>
                                {horaIni || horaFin
                                  ? `${formatTime(horaIni)} - ${formatTime(horaFin)}`
                                  : "-"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {tieneTablaSemana ? (
                    <div style={{ marginTop: 14 }}>{renderScheduleTable(grupo.cortes[0])}</div>
                  ) : null}

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 12,
                      marginTop: expandirCard ? "auto" : 20,
                      paddingTop: 20,
                    }}
                  >
                    {puedeMapa ? (
                      <IonButton
                        expand="block"
                        fill="outline"
                        onClick={() => abrirEnMapa(grupo.coordenadas, grupo.direccion_ps)}
                        style={{ flex: 1, minWidth: 140, height: 48, fontSize: 15 }}
                      >
                        Ver en mapa
                      </IonButton>
                    ) : null}
                    {estadoConf === "PENDIENTE" && pendientes.length > 0 ? (
                      <IonButton
                        expand="block"
                        color="primary"
                        disabled={confirmandoCabId === cabClave || rechazandoCabId === cabClave}
                        onClick={() => handleConfirmarGrupo(grupo)}
                        style={{ flex: 1, minWidth: 180, height: 48, fontSize: 15, fontWeight: 600 }}
                      >
                        {confirmandoCabId === cabClave
                          ? "Confirmando..."
                          : "Confirmar Asistencia"}
                      </IonButton>
                    ) : null}
                    {estadoConf === "PENDIENTE" && pendientes.length > 0 ? (
                      <IonButton
                        expand="block"
                        color="danger"
                        fill="outline"
                        disabled={confirmandoCabId === cabClave || rechazandoCabId === cabClave}
                        onClick={() => setGrupoARechazar(grupo)}
                        style={{ flex: 1, minWidth: 140, height: 48, fontSize: 15, fontWeight: 600 }}
                      >
                        {rechazandoCabId === cabClave ? "Rechazando..." : "Rechazar"}
                      </IonButton>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <IonAlert
          isOpen={!!grupoARechazar}
          onDidDismiss={() => setGrupoARechazar(null)}
          header="Rechazar reemplazo"
          message="Indica el motivo del rechazo (obligatorio)."
          inputs={[
            {
              name: "motivo",
              type: "textarea",
              placeholder: "Motivo del rechazo",
            },
          ]}
          buttons={[
            { text: "Cancelar", role: "cancel" },
            {
              text: "Rechazar",
              handler: (data) => {
                const motivo = String(data?.motivo || "").trim();
                if (!motivo) {
                  present({
                    message: "El motivo del rechazo es obligatorio.",
                    duration: 3000,
                    color: "warning",
                  });
                  return false;
                }
                handleRechazarGrupo(grupoARechazar, motivo);
                return true;
              },
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default MisReemplazos;
